import React from 'react';

import {
  Box,
  Text,
  Input,
  FormControl,
  FormLabel,
  VStack,
  HStack,
  InputGroup,
  InputLeftAddon,
  Textarea,
  useColorModeValue,
  Divider,
} from '@chakra-ui/react';

const SupplierInfoCard = ({ selectedSupplier }) => {
  const bgColor = useColorModeValue('white', 'navy.800');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const headingColor = useColorModeValue('navy.700', 'white');
  const labelColor = useColorModeValue('gray.600', 'gray.300');

  const readOnlyBg = useColorModeValue('gray.50', 'navy.900');
  const readOnlyColor = useColorModeValue('gray.800', 'whiteAlpha.900');

  // Convert supplier prices array to JSON object
  const formatSupplierPrices = React.useMemo(() => {
    if (!selectedSupplier?.prices || !Array.isArray(selectedSupplier.prices)) {
      return JSON.stringify({}, null, 2);
    }

    const pricesObj = {};
    selectedSupplier.prices.forEach((priceItem) => {
      if (priceItem.key && priceItem.value !== undefined) {
        pricesObj[priceItem.key] = priceItem.value;
      }
    });

    return JSON.stringify(pricesObj, null, 2);
  }, [selectedSupplier?.prices]);

  if (!selectedSupplier) {
    return (
      <Box
        bg={bgColor}
        borderRadius="xl"
        p={6}
        border="1px solid"
        borderColor={borderColor}
        flex="1"
        minH="100%"
      >
        <Text fontSize="xl" fontWeight="semibold" color={headingColor} mb={6}>
          Supplier Information
        </Text>
        <Text color={labelColor} fontSize="sm" fontStyle="italic">
          No supplier selected
        </Text>
      </Box>
    );
  }

  const supplierData = {
    name: selectedSupplier.name || selectedSupplier.altName || 'Unknown Supplier',
    prices: formatSupplierPrices,
    availableQuantity: selectedSupplier.stock?.toString() || '0',
    externalShippingCost:
      selectedSupplier.syncedDetails?.externalProvidedShippingCost?.toString() || '0.00',
    sellingPrice: selectedSupplier.sellingPrice?.toString() || '0.00',
    appliedFormula:
      selectedSupplier.syncedDetails?.appliedSellingPriceFormula ||
      'No formula available',
    totalCost: selectedSupplier.cost?.toString() || '0.00',
  };

  return (
    <Box
      bg={bgColor}
      borderRadius="xl"
      p={6}
      border="1px solid"
      borderColor={borderColor}
      flex="1"
      minH="100%"
    >
      <Text fontSize="xl" fontWeight="semibold" color={headingColor} mb={6}>
        Supplier Information - {supplierData.name}
      </Text>

      <VStack spacing={5} align="stretch">
        {/* Prices JSON */}
        <FormControl>
          <FormLabel fontSize="sm" fontWeight="semibold" color={labelColor} mb={2}>
            Prices
          </FormLabel>
          <Textarea
            value={supplierData.prices}
            isReadOnly
            minRows={15}
            minH="150px"
            resize="vertical"
            fontFamily="monospace"
            fontSize="sm"
            borderRadius="md"
            _focus={{
              borderColor: 'blue.400',
              boxShadow: '0 0 0 1px #3182ce',
            }}
            bg={readOnlyBg}
            color={readOnlyColor}
          />
        </FormControl>

        <HStack spacing={5} align="stretch">
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="semibold" color={labelColor} mb={2}>
              Total Cost
            </FormLabel>
            <InputGroup>
              <InputLeftAddon children="$" />
              <Input
                value={supplierData.totalCost}
                isReadOnly
                bg={readOnlyBg}
                color={readOnlyColor}
                size="md"
                borderRadius="md"
              />
            </InputGroup>
          </FormControl>

          <FormControl>
            <FormLabel fontSize="sm" fontWeight="semibold" color={labelColor} mb={2}>
              Selling Price
            </FormLabel>
            <InputGroup>
              <InputLeftAddon children="$" />
              <Input
                value={supplierData.sellingPrice}
                isReadOnly
                bg={readOnlyBg}
                color={readOnlyColor}
                size="md"
                borderRadius="md"
              />
            </InputGroup>
          </FormControl>
        </HStack>

        <HStack spacing={5} align="stretch">
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="semibold" color={labelColor} mb={2}>
              External Provided Shipping Cost
            </FormLabel>
            <InputGroup>
              <InputLeftAddon children="$" />
              <Input
                value={supplierData.externalShippingCost}
                isReadOnly
                bg={readOnlyBg}
                color={readOnlyColor}
                size="md"
                borderRadius="md"
              />
            </InputGroup>
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="semibold" color={labelColor} mb={2}>
              Available Quantity
            </FormLabel>
            <InputGroup>
              <InputLeftAddon children="#" />
              <Input
                value={supplierData.availableQuantity}
                isReadOnly
                bg={readOnlyBg}
                color={readOnlyColor}
                size="md"
                borderRadius="md"
              />
            </InputGroup>
          </FormControl>
        </HStack>

        <Divider />

        <FormControl>
          <FormLabel fontSize="sm" fontWeight="semibold" color={labelColor} mb={2}>
            Applied Selling Price Formula
          </FormLabel>
          <Input
            value={supplierData.appliedFormula}
            isReadOnly
            bg={readOnlyBg}
            color={readOnlyColor}
            size="md"
            borderRadius="md"
            fontFamily="monospace"
            fontSize="sm"
          />
        </FormControl>
      </VStack>
    </Box>
  );
};

export default SupplierInfoCard;
